import makeContext from '@libs/makeContext';
import { DASHBOARD_OVERVIEW_INITIAL_API_PARAMS } from '../../common';


import {
    SET_DATE,
    SET_BRANCH,
    SET_MODAL_CLOSE,
    SET_FILTERS_OVERVIEW_ATTENDANCE,
    SET_FILTERS_OVERVIEW_LEAVE
} from '../../context.actionTypes';
import { normalizeIso8601DateOnly } from '@libs/dateTime';

const initialState = {
    openModal: false,
    ...DASHBOARD_OVERVIEW_INITIAL_API_PARAMS
};

/**
 * Request filters behind each summary box.
 *
 * 'remote' and 'inOffice' are the two halves of that day's attendance, so both
 * need status 'in' alongside the flag -- the endpoint only defaults status for
 * joinedRemotely=true, and without it 'inOffice' would also return absentees.
 */
const attendanceFilters = (status, branch) => {
    switch (status) {
        case 'remote':
            return { branch, status: 'in', joinedRemotely: true };
        case 'inOffice':
            return { branch, status: 'in', joinedRemotely: false };
        case 'all':
            return { branch, status: '' };
        default:
            return { branch, status };
    }
};

const reducer = (state, { type, payload }) => {
    switch (type) {
        case SET_DATE: {
            return {
                ...state,
                date: payload,
            };
        }
        case SET_BRANCH: {
            return {
                ...state,
                branch: payload
            };
        }
        case SET_FILTERS_OVERVIEW_ATTENDANCE: {
            return {
                ...state,
                status: payload,
                openModal: true
            };
        }

        case SET_FILTERS_OVERVIEW_LEAVE: {
            return {
                ...state,
                status: payload,
                openModal: true
            };
        }
        case SET_MODAL_CLOSE: {
            return {
                ...state,
                openModal: false
            };
        }
        default:
            return state;
    }
};

const middleware = (state, action, { fetchData, fetchAttendanceData, fetchLeaveData }) => {
    switch (action.type) {

        // Every outgoing date is date-only, for the reason spelled out on the
        // modal cases below: a zone-less timestamp lands a day early once the
        // server re-reads it in its own timezone. The cards and the modals
        // have to be asking about the same day.
        case SET_DATE: {
            const { branch } = state;
            const { payload } = action;

            fetchData({
                branch,
                date: normalizeIso8601DateOnly(payload),
            });

            return action;
        }

        case SET_BRANCH: {
            const { date } = state;
            const { payload } = action;

            fetchData({
                date: normalizeIso8601DateOnly(date),
                branch: payload
            });

            return action;
        }

        /*
         * Date-only, not normalizeIso8601Date. That helper emits
         * 'YYYY-MM-DDTHH:mm:ss' with no zone, which `new Date()` on the server
         * reads as *its* local time: at UTC+6 that is 18:00 the previous day,
         * and the repository then does toISOString().split('T')[0], so the
         * query lands a day early and finds nothing. A bare 'YYYY-MM-DD' is
         * parsed as UTC by spec and survives the round trip -- which is why
         * the summary cards, which already send date-only, were counting rows
         * the modal could not list.
         */
        case SET_FILTERS_OVERVIEW_ATTENDANCE: {
            const { date, branch } = state;
            const { payload } = action;

            fetchAttendanceData({
                date: normalizeIso8601DateOnly(date),
                filters: attendanceFilters(payload, branch)
            });

            return action;
        }

        case SET_FILTERS_OVERVIEW_LEAVE: {
            const { date, branch } = state;

            // Every category in one call -- the modal groups them itself.
            fetchLeaveData({
                date: normalizeIso8601DateOnly(date),
                filters: {
                    branch: branch,
                }
            });

            return action;
        }
        default:
            return action;
    }
};

const [
    OverviewProvider,
    useOverviewState,
    useOverviewDispatch,
    useOverview
] = makeContext(reducer, initialState, {
    name: 'Overview',
    middleware
});

export {
    useOverview as default,
    OverviewProvider,
    useOverviewState,
    useOverviewDispatch,
    SET_DATE,
    SET_BRANCH,
    SET_MODAL_CLOSE
};
